/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

// ── Right-panel docked-rail width (WIDE layout only) ───────────────────────────
//
// On WIDE the panel docks as a right rail whose width the user drags via the
// shell's left-edge handle (see `RightPanelResizeHandle`). This is shared chrome
// of the right panel, so ONE width drives EVERY surface (chat, queue): the chosen
// width feeds the `--right-panel-width` CSS var (published on the document root by
// `bindRightPanelWidthVar`), which BOTH the panel's own width AND `#main-content`'s
// reserved right-inset read — so page and panel track together as the handle
// drags. NARROW ignores it (the panel is fullscreen).

import { data, type ObservableValue } from '@amazon/vinyl-observable'
import { createPersistentItem, type Unsubscribe } from '@amazon/vinyl-util'
import { number } from '@amazon/vinyl-validation'
import { ctx } from '@/util/context'
import { globalData } from '@amzn/bf-playback'

const { errorHandler } = ctx('rightPanelWidth')

/** Below MIN the composer + header controls crowd; above MAX the rail eats the
 * page. Default matches the original fixed rail width. */
export const MIN_RIGHT_PANEL_WIDTH = 320
export const MAX_RIGHT_PANEL_WIDTH = 640
export const DEFAULT_RIGHT_PANEL_WIDTH = 420

/**
 * Clamp a candidate rail width to the sensible range. When `viewportWidth` is
 * given, the rail is additionally capped at 60% of the viewport so a
 * just-barely-wide screen keeps a usable content column; that per-viewport cap
 * never drops below MIN.
 */
export function clampRightPanelWidth(width: number, viewportWidth?: number): number {
    const upper =
        viewportWidth != null
            ? Math.max(MIN_RIGHT_PANEL_WIDTH, Math.min(MAX_RIGHT_PANEL_WIDTH, viewportWidth * 0.6))
            : MAX_RIGHT_PANEL_WIDTH
    return Math.round(Math.min(upper, Math.max(MIN_RIGHT_PANEL_WIDTH, width)))
}

const width$ = globalData(() => data(DEFAULT_RIGHT_PANEL_WIDTH))

/** Reactive docked-rail width in px (WIDE layout). */
export const rightPanelWidth$: ObservableValue<number> = width$

const persistedWidth = createPersistentItem<number>({
    key: 'rightPanelWidth',
    defaultValue: DEFAULT_RIGHT_PANEL_WIDTH,
    validator: number().assert,
})

/** Load the persisted rail width, clamped to the current range. */
export async function hydrateRightPanelWidth(): Promise<void> {
    width$.value = clampRightPanelWidth(await persistedWidth.get())
}

/**
 * Commit a new rail width: clamp, update the reactive value, and persist. A
 * no-op write (same clamped value) is coalesced so it doesn't churn storage.
 */
export function setRightPanelWidth(width: number, viewportWidth?: number): void {
    const next = clampRightPanelWidth(width, viewportWidth)
    if (width$.value === next) return
    width$.value = next
    persistedWidth.set(next).catch(errorHandler)
}

/**
 * Publish the docked-rail width as the `--right-panel-width` :root var so the
 * panel's own width AND `#main-content`'s reserved right-inset read one live
 * value, and restore the persisted width once. App-root singleton (both surfaces
 * mount at once), so the var is owned in ONE place rather than per-surface.
 * Returns a disposer for `onConnect`.
 */
export function bindRightPanelWidthVar(): Unsubscribe {
    const sub = width$.onData((width) => {
        document.documentElement.style.setProperty('--right-panel-width', `${width}px`)
    })
    hydrateRightPanelWidth().catch(errorHandler)
    return sub
}
